import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { api } from 'controllers/network/apiClient';

export const SUBSCRIBER_AI_ELIGIBILITY_QUERY_KEY = 'subscriber-ai-eligibility';

export const AIEligibilityReason = {
  NO_PROFILE: 'no_profile',
  NO_CONSENTS: 'no_consents',
  NO_ELIGIBLE_CONSENT: 'no_eligible_consent',
} as const;

export type AIEligibilityReason =
  typeof AIEligibilityReason[keyof typeof AIEligibilityReason];

export const AIEligibilityConsent = {
  POSTSCRIPT_PLUS: 'postscript_plus',
  AI: 'ai',
} as const;

export type AIEligibilityConsent =
  typeof AIEligibilityConsent[keyof typeof AIEligibilityConsent];

export interface SubscriberAIEligibility {
  /**
   * `true` / `false` when the eligibility could be determined; `null` when
   * profile-service was unreachable. UI should render `null` as "unknown".
   */
  eligible: boolean | null;
  reason: AIEligibilityReason | null;
  matchedConsents: AIEligibilityConsent[];
  /**
   * ISO-8601 timestamp of the most recent eligible consent on the profile
   * (`ai` or `postscript_plus`). `null` when there's no eligible consent or
   * when the timestamp is unknown.
   */
  lastConsentAt: string | null;
}

export const useSubscriberAiEligibility = (
  subscriberId?: number,
): UseQueryResult<SubscriberAIEligibility> =>
  useQuery({
    queryKey: [SUBSCRIBER_AI_ELIGIBILITY_QUERY_KEY, subscriberId],
    queryFn: () =>
      api.get<SubscriberAIEligibility>(
        `/v2/ai-components/eligibility/subscribers/${subscriberId}`,
      ),
    enabled: !!subscriberId,
    staleTime: 60_000,
  });

export default useSubscriberAiEligibility;
